import {
  getGlobalDefaultDate,
  isDateBeforeGlobalMin,
  isGlobalMinDateExcluded,
} from '@/core/config/DatePickerConfig';
import { ElDatePicker } from 'element-plus';
import {
  App,
  defineComponent,
  h,
  nextTick,
  onBeforeUnmount,
  ref,
  watch,
} from 'vue';

type DisabledDateFn = (time: Date) => boolean;
type DefaultValue = Date | Date[] | undefined;
type ModelUpdater = (value: Date | Date[] | string | null) => void;

type PickerInstance = InstanceType<typeof ElDatePicker> & {
  $el?: unknown;
  refPopper?: {
    triggerRef?: HTMLElement | { $el?: unknown };
  };
};

const ENTER_HANDLER_KEY = '__vtuDatePickerEnterHandler';

const resolveDefaultValue = (attrs: Record<string, unknown>): Date | Date[] => {
  const originalDefaultValue = (attrs.defaultValue ??
    attrs['default-value']) as DefaultValue;

  if (originalDefaultValue) {
    return originalDefaultValue;
  }

  if (isGlobalMinDateExcluded(attrs)) {
    return new Date();
  }

  return getGlobalDefaultDate();
};

const stripCustomAttrs = (attrs: Record<string, unknown>) => {
  const restAttrs = { ...attrs };

  delete restAttrs.allowBeforeGlobalMin;
  delete restAttrs['allow-before-global-min'];
  delete restAttrs.disabledDate;
  delete restAttrs['disabled-date'];
  delete restAttrs.defaultValue;
  delete restAttrs['default-value'];
  // ElPopper (Picker root) is a fragment — forwarding tabindex triggers a Vue warn.
  delete restAttrs.tabindex;
  delete restAttrs.tabIndex;

  return restAttrs;
};

const asHtmlElement = (value: unknown): HTMLElement | null => {
  if (value instanceof HTMLElement) {
    return value;
  }

  if (
    value &&
    typeof value === 'object' &&
    '$el' in value &&
    (value as { $el: unknown }).$el instanceof HTMLElement
  ) {
    return (value as { $el: HTMLElement }).$el;
  }

  return null;
};

/**
 * ElPopper renders a Fragment, so picker.$el is often a comment placeholder
 * and querySelector on it fails. Resolve the real trigger input instead.
 */
const resolvePickerInputs = (
  picker: PickerInstance | null,
  attrs: Record<string, unknown>
): HTMLInputElement[] => {
  if (!picker) {
    return [];
  }

  const triggerEl = asHtmlElement(picker.refPopper?.triggerRef);

  if (triggerEl) {
    const fromTrigger = Array.from(
      triggerEl.querySelectorAll('input')
    ) as HTMLInputElement[];

    if (fromTrigger.length) {
      return fromTrigger;
    }
  }

  const root = asHtmlElement(picker.$el);

  if (root) {
    const fromRoot = Array.from(
      root.querySelectorAll('input')
    ) as HTMLInputElement[];

    if (fromRoot.length) {
      return fromRoot;
    }
  }

  // Fragment placeholder: search siblings under the same parent.
  const anchor = picker.$el;

  if (anchor instanceof Node && anchor.parentElement) {
    const fromParent = Array.from(
      anchor.parentElement.querySelectorAll<HTMLInputElement>(
        '.el-date-editor input.el-input__inner, .el-date-editor input'
      )
    );

    if (fromParent.length === 1) {
      return fromParent;
    }
  }

  const name = attrs.name;

  if (typeof name === 'string' && name) {
    return Array.from(
      document.querySelectorAll<HTMLInputElement>(
        `input[name="${CSS.escape(name)}"]`
      )
    );
  }

  return [];
};

const applyTabIndexToInput = (
  picker: PickerInstance | null,
  attrs: Record<string, unknown>,
  tabIndex: unknown
) => {
  const inputs = resolvePickerInputs(picker, attrs);

  if (!inputs.length) {
    return;
  }

  inputs.forEach((input) => {
    if (tabIndex === undefined || tabIndex === null || tabIndex === '') {
      input.removeAttribute('tabindex');
      return;
    }

    input.setAttribute('tabindex', String(tabIndex));
  });
};

const isModelEmpty = (value: unknown): boolean => {
  if (value == null || value === '') {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length === 0 || value.every((item) => item == null || item === '');
  }

  return false;
};

const isRangeOrMultiType = (attrs: Record<string, unknown>): boolean => {
  const type = String(attrs.type ?? 'date');

  return type.includes('range') || type === 'dates';
};

const isPickerPanelOpen = (input: HTMLInputElement): boolean => {
  const editor = input.closest('.el-date-editor');

  if (editor?.classList.contains('is-active')) {
    return true;
  }

  return Boolean(
    document.querySelector(
      '.el-picker__popper[aria-hidden="false"], .el-picker__popper:not([style*="display: none"])'
    )
  );
};

/**
 * Confirm the calendar's highlighted/default day via a real cell click so
 * Element Plus runs its normal pick → close flow (valueFormat, events, etc.).
 */
const clickHighlightedDateCell = (): boolean => {
  const panel = Array.from(
    document.querySelectorAll<HTMLElement>('.el-picker__popper')
  ).find((el) => {
    const style = window.getComputedStyle(el);

    return (
      el.getAttribute('aria-hidden') !== 'true' &&
      style.display !== 'none' &&
      style.visibility !== 'hidden'
    );
  });

  if (!panel) {
    return false;
  }

  const cell =
    panel.querySelector<HTMLElement>(
      'td.available.current:not(.disabled), td.current.available:not(.disabled)'
    ) ||
    panel.querySelector<HTMLElement>(
      'td.available.today:not(.disabled), td.today.available:not(.disabled)'
    );

  if (!cell) {
    return false;
  }

  cell.click();

  return true;
};

const resolveModelUpdater = (
  attrs: Record<string, unknown>
): ModelUpdater | null => {
  const updater = attrs['onUpdate:modelValue'] ?? attrs.onUpdateModelValue;

  return typeof updater === 'function' ? (updater as ModelUpdater) : null;
};

const resolveModelValue = (attrs: Record<string, unknown>): unknown => {
  return attrs.modelValue ?? attrs['model-value'];
};

const isEnterKey = (event: KeyboardEvent): boolean => {
  return (
    event.key === 'Enter' ||
    event.code === 'Enter' ||
    event.code === 'NumpadEnter'
  );
};

export function initGlobalDatePicker(app: App<Element>) {
  const GlobalDatePicker = defineComponent({
    name: 'ElDatePicker',
    inheritAttrs: false,
    setup(_, { attrs, slots, expose }) {
      const pickerRef = ref<PickerInstance | null>(null);
      const boundInputs = new Set<HTMLInputElement>();

      const mergedDisabledDate = (time: Date) => {
        if (!isGlobalMinDateExcluded(attrs) && isDateBeforeGlobalMin(time)) {
          return true;
        }

        const originalDisabledDate = (attrs.disabledDate ??
          attrs['disabled-date']) as DisabledDateFn | undefined;

        if (originalDisabledDate) {
          return originalDisabledDate(time);
        }

        return false;
      };

      const confirmDefaultDate = () => {
        if (clickHighlightedDateCell()) {
          return;
        }

        const updater = resolveModelUpdater(attrs as Record<string, unknown>);
        const defaultValue = resolveDefaultValue(
          attrs as Record<string, unknown>
        );

        if (!updater || Array.isArray(defaultValue)) {
          return;
        }

        if (mergedDisabledDate(defaultValue)) {
          return;
        }

        updater(defaultValue);
      };

      const onInputEnterKeydown = (event: KeyboardEvent) => {
        if (!isEnterKey(event)) {
          return;
        }

        // Element Plus does not preventDefault — without this, Enter submits the form.
        event.preventDefault();

        const input = event.currentTarget as HTMLInputElement;

        if (isRangeOrMultiType(attrs as Record<string, unknown>)) {
          return;
        }

        if (!isPickerPanelOpen(input)) {
          return;
        }

        // User typed a value — let EP parse/confirm via its own handler.
        if (input.value.trim()) {
          return;
        }

        if (!isModelEmpty(resolveModelValue(attrs as Record<string, unknown>))) {
          return;
        }

        confirmDefaultDate();
      };

      const detachEnterHandlers = () => {
        boundInputs.forEach((input) => {
          const handler = (
            input as HTMLInputElement & {
              [ENTER_HANDLER_KEY]?: (event: KeyboardEvent) => void;
            }
          )[ENTER_HANDLER_KEY];

          if (handler) {
            input.removeEventListener('keydown', handler, true);
            delete (
              input as HTMLInputElement & {
                [ENTER_HANDLER_KEY]?: (event: KeyboardEvent) => void;
              }
            )[ENTER_HANDLER_KEY];
          }
        });

        boundInputs.clear();
      };

      const syncEnterHandlers = () => {
        const inputs = resolvePickerInputs(
          pickerRef.value,
          attrs as Record<string, unknown>
        );
        const nextInputs = new Set(inputs);

        boundInputs.forEach((input) => {
          if (nextInputs.has(input)) {
            return;
          }

          const handler = (
            input as HTMLInputElement & {
              [ENTER_HANDLER_KEY]?: (event: KeyboardEvent) => void;
            }
          )[ENTER_HANDLER_KEY];

          if (handler) {
            input.removeEventListener('keydown', handler, true);
            delete (
              input as HTMLInputElement & {
                [ENTER_HANDLER_KEY]?: (event: KeyboardEvent) => void;
              }
            )[ENTER_HANDLER_KEY];
          }

          boundInputs.delete(input);
        });

        inputs.forEach((input) => {
          const typedInput = input as HTMLInputElement & {
            [ENTER_HANDLER_KEY]?: (event: KeyboardEvent) => void;
          };

          if (typedInput[ENTER_HANDLER_KEY]) {
            boundInputs.add(input);
            return;
          }

          typedInput[ENTER_HANDLER_KEY] = onInputEnterKeydown;
          input.addEventListener('keydown', onInputEnterKeydown, true);
          boundInputs.add(input);
        });
      };

      const syncTabIndex = () => {
        applyTabIndexToInput(
          pickerRef.value,
          attrs as Record<string, unknown>,
          attrs.tabindex ?? attrs.tabIndex
        );
      };

      const syncInputBindings = () => {
        syncTabIndex();
        syncEnterHandlers();
      };

      const focus = () => {
        syncInputBindings();
        const input = resolvePickerInputs(
          pickerRef.value,
          attrs as Record<string, unknown>
        )[0];

        input?.focus();
      };

      expose(
        new Proxy(
          {},
          {
            get(_target, key) {
              if (key === 'focus') {
                return focus;
              }

              return Reflect.get(pickerRef.value ?? {}, key);
            },
          }
        )
      );

      watch(
        () => [attrs.tabindex, attrs.tabIndex, pickerRef.value] as const,
        () => {
          nextTick(syncInputBindings);
        },
        { flush: 'post' }
      );

      onBeforeUnmount(() => {
        detachEnterHandlers();
      });

      return () => {
        return h(
          ElDatePicker,
          {
            ...stripCustomAttrs(attrs as Record<string, unknown>),
            ref: pickerRef,
            defaultValue: resolveDefaultValue(attrs as Record<string, unknown>),
            disabledDate: mergedDisabledDate,
            onVnodeMounted: () => {
              nextTick(syncInputBindings);
            },
            onVnodeUpdated: () => {
              nextTick(syncInputBindings);
            },
          },
          slots
        );
      };
    },
  });

  // Replace Element Plus registration quietly (app.component would warn on overwrite).
  const components = app._context.components;
  components.ElDatePicker = GlobalDatePicker;
  components['el-date-picker'] = GlobalDatePicker;
}
